import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getFieldPropertyValues } from './helpers';
import * as FormFieldInputs from './form-field-inputs';
import ValidationMessage from './validation-message';

export type FormFieldInputType = keyof typeof InputTypes;

export type FormFieldPropertyType =
  | 'label'
  | 'makeRequired'
  | 'makeHidden'
  | 'hideLabel'
  | 'readOnly'
  | 'sublabel'
  | 'placeholder'
  | 'isVisible';

export type FormFieldPropertyConfig = [
    FormFieldPropertyType,
  { type: string; label: string; value: any; isDynamic: boolean },
];

export type FormFieldInputConfig = {
  id?: FormFieldInputType | number; // There's code that under some nebulous condition expects this to match one of the predefined input types but otherwise is just a normal number...
  uuid?: string;
  is_basic: boolean;
  type_name: string;
  account_id: string | null;
  group_name: string;
  is_deleted: boolean | null;
  is_enabled: boolean | null;
  is_visible: boolean | null;
  is_syncable: boolean | null;
  value?: any;
  sort_order: number;
  display_name: string;
  legwork_data_type: string;
  form_field_type_id?: FormFieldInputTypeEnum;
  form_field_group_id?: number;
  legwork_table_name: string;
  legwork_column_name: string;
  properties:
    | FormFieldPropertyConfig[]
    | { common: FormFieldPropertyConfig[]; specific: FormFieldPropertyConfig[] };
};

export type FormFieldGroupConfig = {
  id?: number;
  uuid?: string;
  fields: FormFieldInputConfig[];
  properties: { common: FormFieldPropertyConfig[]; specific?: FormFieldPropertyConfig[] };
  sort_order: number;
  display_name: string;
  validationMessage: string | string[] | null;
  form_field_group_id: number | null;
};

export type FormFieldConfig = FormFieldGroupConfig | FormFieldInputConfig;

export enum FormFieldInputTypeEnum {
  DatePicker = 1,
  Time = 2,
  Number = 3,
  ShortTextEntry = 4,
  LongTextEntry = 5,
  Dropdown = 6,
  SingleChoice = 7,
  MultipleChoice = 8,
  Spinner = 9,
  Signature = 10,
  Checkbox = 11,
};

export const InputTypes = {
  [`${FormFieldInputTypeEnum.DatePicker}`]: (props: any) => <FormFieldInputs.FormFieldInputDate {...props} />,
  [`${FormFieldInputTypeEnum.Time}`]: (props: any) => <FormFieldInputs.FormFieldInputTime {...props} />,
  [`${FormFieldInputTypeEnum.Number}`]: (props: any) => <FormFieldInputs.FormFieldInputText type="number" {...props} />,
  [`${FormFieldInputTypeEnum.ShortTextEntry}`]: (props: any) => <FormFieldInputs.FormFieldInputText type="text" {...props} />,
  [`${FormFieldInputTypeEnum.LongTextEntry}`]: (props: any) => <FormFieldInputs.FormFieldInputTextArea {...props} />,
  [`${FormFieldInputTypeEnum.Dropdown}`]: (props: any) => <FormFieldInputs.FormFieldInputSelect {...props} />,
  [`${FormFieldInputTypeEnum.SingleChoice}`]: (props: any) => <FormFieldInputs.FormFieldInputRadio {...props} />,
  [`${FormFieldInputTypeEnum.MultipleChoice}`]: (props: any) => <FormFieldInputs.FormFieldInputCheckbox {...props} />,
  [`${FormFieldInputTypeEnum.Spinner}`]: (props: any) => (
    <FormFieldInputs.FormFieldInputText
      type="number"
      className="span30 spinner"
      {...props}
    />
  ),
  [`${FormFieldInputTypeEnum.Checkbox}`]: (props: any) => <FormFieldInputs.FormFieldInputCheckbox {...props} />,
};

export type FormFieldInputProps = { 
  field: FormFieldInputConfig,
  options: any,
  children?: boolean | React.ReactNode,
  readOnly?: boolean
};

export const FormFieldInput: React.FC<FormFieldInputProps> = ({ field, options, children }) => {
  const renderInput = (field: FormFieldInputConfig, props: any): JSX.Element | undefined => {
    const inputTypeIndex = field?.form_field_type_id || field?.id;
    
    if (inputTypeIndex === undefined) return undefined;
    
    // @ts-ignore
    return InputTypes[inputTypeIndex](props);
  }
    

  const {
    properties,
    subfield,
    placeholder,
    sublabel,
    isRenderedField,
    onChange,
    validationMessage,
    readOnly,
    style,
    ...rest
  } = options;
  const propertyNames = [
    'label',
    'makeRequired',
    'makeHidden',
    'hideLabel',
    'readOnly',
    'sublabel',
    'placeholder',
  ];
  const defaultValues = {
    label: field.display_name,
    makeRequired: false,
    makeHidden: false,
    hideLabel: false,
    readOnly: false,
    placeholder: '',
  };
  const commonPropValues: Record<string, any> = getFieldPropertyValues(
    properties.common || properties,
    propertyNames,
    defaultValues
  );
  const containerClasses = classNames({
    'form-field-input-container': true,
    'hide-field': commonPropValues.makeHidden,
    'hide-label': commonPropValues.hideLabel || properties.hideLabel,
    'read-only': commonPropValues.readOnly || readOnly,
    'multiple': properties.multiple,
  });
  const inputClasses = classNames({
    'form-field-input': true,
    text: field.type_name === 'Short Text Entry',
    textarea: field.type_name === 'Long Text Entry',
    select: field.type_name === 'Dropdown',
  });
  const validationClasses = classNames({
    'control-group error': validationMessage,
  });
  const id = field.uuid;

  return (
    <div key={id} className={containerClasses}>
      {!subfield ? (
        <label htmlFor={id} className={'form-field-label primary'}>{`${
          commonPropValues.label
        } ${commonPropValues.makeRequired ? '*' : ''}`}</label>
      ) : null}
      <div className={inputClasses}>
        <div className={validationClasses} style={style ? style : undefined}>
          {renderInput(field, {
            id,
            placeholder: placeholder || commonPropValues.placeholder,
            isRenderedField,
            onChange,
            children,
            disabled: readOnly || commonPropValues.readOnly,
            readOnly,
            ...rest,
          })}
          {sublabel || commonPropValues.sublabel ? (
            <label htmlFor={id} className={'form-field-label secondary'}>
              {sublabel || commonPropValues.sublabel}
            </label>
          ) : null}
          <ValidationMessage message={validationMessage} />
        </div>
      </div>
    </div>
  );
};

export default FormFieldInput;

// @ts-ignore
FormFieldInput.propTypes = {
  // @ts-ignore
  field: PropTypes.object.isRequired,
  options: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.bool,
  ]),
  readOnly: PropTypes.bool,
};

FormFieldInput.defaultProps = {
  options: {},
  children: null,
  readOnly: false,
};
